import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useReviewers,
  useAssignReviewers,
  useUnassignReviewer,
  useSubmitReviewerDecision,
  useAssignableReviewers,
} from '../../hooks/useReviews'
import { useCurrentUser } from '../AuthGuard/AuthGuard'
import type { Reviewer } from '../../gen/types'

interface Props {
  reviewId: string
  canAssign: boolean
}

// ReviewerPanel renders the per-reviewer chips + the "+ assign…" trigger
// inline inside the meta card. The trigger's dropdown is portaled to
// document.body so the meta card's overflow can't clip it.
//
// No aggregate counter here — the PR band's top-right pill conveys overall
// state. Pure chip strip keeps the meta row compact.
export default function ReviewerPanel({ reviewId, canAssign }: Props) {
  const me = useCurrentUser()
  const { data, isLoading, error } = useReviewers(reviewId)
  const assignMut = useAssignReviewers(reviewId)
  const unassignMut = useUnassignReviewer(reviewId)
  const decisionMut = useSubmitReviewerDecision(reviewId)

  const [popOpen, setPopOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popPos, setPopPos] = useState<{ top: number; left: number } | null>(null)

  const { data: candidates = [] } = useAssignableReviewers(reviewId, popOpen)

  // Click-away to close the assign popover.
  useEffect(() => {
    if (!popOpen) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setPopOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [popOpen])

  // Position the portaled popover under the trigger. Listeners track
  // scroll/resize so it stays anchored when the page shifts.
  useLayoutEffect(() => {
    if (!popOpen) {
      setPopPos(null)
      return
    }
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      setPopPos({ top: r.bottom + 4, left: r.left })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [popOpen])

  const reviewers = data?.reviewers ?? []

  const filteredCandidates = useMemo(() => {
    const q = filter.trim().replace(/^@/, '').toLowerCase()
    const taken = new Set(reviewers.map((r) => r.userId))
    return candidates.filter((c) => {
      if (taken.has(c.userId)) return false
      if (!q) return true
      return (
        c.githubLogin.toLowerCase().includes(q) ||
        c.displayName.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q)
      )
    })
  }, [filter, candidates, reviewers])

  // GitHub-lookup fallback is offered when the typed query matches no
  // tenant member — they're probably reaching for someone outside the org.
  const lookupTarget = useMemo(() => {
    const q = filter.trim().replace(/^@/, '')
    if (!q) return ''
    if (filteredCandidates.some((c) => c.githubLogin.toLowerCase() === q.toLowerCase())) return ''
    return q
  }, [filter, filteredCandidates])

  if (isLoading) return <span className="text-fg-dim text-[12px]">loading…</span>
  if (error || !data) return null

  const handleAssign = async (login: string) => {
    const clean = login.trim().replace(/^@/, '')
    if (!clean) return
    try {
      await assignMut.mutateAsync([clean])
      setFilter('')
      setPopOpen(false)
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert((e as Error).message)
    }
  }

  const handleDecision = (action: 'approve' | 'request_changes') => {
    decisionMut.mutate({ action, comment: '' })
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-[8px]">
      {reviewers.length === 0 && (
        <span className="text-fg-dim text-[12px]">none assigned</span>
      )}

      {reviewers.map((r) => (
        <ReviewerChip
          key={r.id}
          reviewer={r}
          isMe={me?.userId === r.userId}
          onApprove={() => handleDecision('approve')}
          onRequestChanges={() => handleDecision('request_changes')}
          onRemove={canAssign ? () => unassignMut.mutate(r.id) : undefined}
          busy={decisionMut.isPending}
        />
      ))}

      {canAssign && (
        <button
          ref={triggerRef}
          type="button"
          className="assign-chip"
          onClick={() => setPopOpen((v) => !v)}
          aria-expanded={popOpen}
        >
          + assign…
        </button>
      )}

      {popOpen && popPos &&
        createPortal(
          <div
            ref={popoverRef}
            className="assign-popover"
            style={{ position: 'fixed', top: popPos.top, left: popPos.left }}
          >
            <input
              className="input"
              placeholder="filter members or type a GitHub login"
              value={filter}
              autoFocus
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (filteredCandidates[0]) handleAssign(filteredCandidates[0].githubLogin)
                  else if (lookupTarget) handleAssign(lookupTarget)
                } else if (e.key === 'Escape') {
                  setPopOpen(false)
                }
              }}
            />
            <div className="flex flex-col gap-[2px] mt-1 max-h-[200px] overflow-y-auto">
              {filteredCandidates.slice(0, 8).map((c) => (
                <button
                  type="button"
                  key={c.userId}
                  className="item"
                  onClick={() => handleAssign(c.githubLogin)}
                >
                  <span className="ic" />
                  <span className="text-mono">@{c.githubLogin}</span>
                  <span className="email">{c.email || c.displayName}</span>
                </button>
              ))}
              {filteredCandidates.length === 0 && !lookupTarget && (
                <div className="text-fg-dim text-[11px] px-[6px] py-[4px]">no matching members</div>
              )}
            </div>
            {lookupTarget && (
              <button type="button" className="lookup" onClick={() => handleAssign(lookupTarget)}>
                ↳ Lookup <span className="text-mono text-fg">@{lookupTarget}</span> on GitHub
              </button>
            )}
          </div>,
          document.body,
        )}
    </span>
  )
}

function ReviewerChip({
  reviewer,
  isMe,
  onApprove,
  onRequestChanges,
  onRemove,
  busy,
}: {
  reviewer: Reviewer
  isMe: boolean
  onApprove: () => void
  onRequestChanges: () => void
  onRemove?: () => void
  busy: boolean
}) {
  const glyph = stateGlyph(reviewer.state)
  return (
    <span className="rv">
      {isMe ? (
        <DecisionMenu state={reviewer.state} onApprove={onApprove} onRequestChanges={onRequestChanges} busy={busy} />
      ) : (
        <span className={`icon ${glyph.cls}`} title={glyph.title}>
          {glyph.char}
        </span>
      )}
      <span className={`text-mono${isMe ? ' rv-self' : ''}`}>
        @{reviewer.githubLogin || reviewer.displayName || '—'}
      </span>
      {onRemove && (
        <button className="rv-remove" onClick={onRemove} aria-label="Remove reviewer">
          ×
        </button>
      )}
    </span>
  )
}

// DecisionMenu wraps the leading glyph on the "you" row in a clickable
// button. Clicking opens a small portaled menu with Approve / Request
// changes options; the current decision is marked. This is the only
// surface for changing your decision — no inline ✓/✗ buttons crowding
// the chip.
function DecisionMenu({
  state,
  onApprove,
  onRequestChanges,
  busy,
}: {
  state: string
  onApprove: () => void
  onRequestChanges: () => void
  busy: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const glyph = stateGlyph(state)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      setPos({ top: r.bottom + 4, left: r.left })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  const pick = (fn: () => void) => {
    fn()
    setOpen(false)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`icon-menu icon ${glyph.cls}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Your decision: ${glyph.title}. Click to change.`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
      >
        {glyph.char}
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            className="decision-menu"
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
          >
            <button
              role="menuitem"
              type="button"
              className={`item${state === 'approved' ? ' is-active' : ''}`}
              onClick={() => pick(onApprove)}
            >
              <span className="icon approved">✓</span> Approve
            </button>
            <button
              role="menuitem"
              type="button"
              className={`item${state === 'changes_requested' ? ' is-active' : ''}`}
              onClick={() => pick(onRequestChanges)}
            >
              <span className="icon changes">✗</span> Request changes
            </button>
          </div>,
          document.body,
        )}
    </>
  )
}

function stateGlyph(state: string): { char: string; cls: string; title: string } {
  switch (state) {
    case 'approved':
      return { char: '✓', cls: 'approved', title: 'approved' }
    case 'changes_requested':
      return { char: '✗', cls: 'changes', title: 'changes requested' }
    case 'dismissed':
      return { char: '⊘', cls: 'dismissed', title: 'dismissed' }
    case 'commented':
      return { char: '●', cls: 'pending', title: 'commented' }
    default:
      return { char: '●', cls: 'pending', title: 'pending' }
  }
}
